<script setup lang="ts">
/**
 * The only input row in the product: label above, 32px control, one hint or error under it.
 * Same control on auth, onboarding and every dialog.
 */
defineOptions({ inheritAttrs: false })
withDefaults(defineProps<{
  label: string
  type?: string
  modelValue?: string
  placeholder?: string
  hint?: string
  error?: string
  autocomplete?: string
  required?: boolean
}>(), { type: 'text', modelValue: '', placeholder: undefined, hint: undefined, error: undefined, autocomplete: undefined })
const emit = defineEmits<{ 'update:modelValue': [v: string] }>()

/** Casts stay out of the template — it must hold paths and calls only. */
const onInput = (event: Event) => emit('update:modelValue', (event.target as HTMLInputElement).value)
</script>

<template>
  <label class="flex flex-col gap-1.5">
    <span class="flex items-center justify-between text-xs text-muted-foreground">
      {{ label }}<slot name="labelAside" />
    </span>
    <input
      v-bind="$attrs"
      :type="type" :value="modelValue" :placeholder="placeholder" :autocomplete="autocomplete" :required="required"
      class="h-8 rounded-[var(--radius-control)] border bg-muted/40 px-2.5 text-[length:var(--type-control)] text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
      :class="error ? 'border-destructive' : 'border-border focus:border-primary/60'"
      @input="onInput"
    >
    <span v-if="error" class="text-[length:var(--type-meta)] text-destructive">{{ error }}</span>
    <span v-else-if="hint" class="num text-[length:var(--type-fine)] text-muted-foreground">{{ hint }}</span>
  </label>
</template>
